import { useEffect, useRef, useState } from 'react';

import { useQuestionInteractionState } from '@/features/content/hooks/useQuestionInteractionState';
import type { UserRole } from '@/features/courses/useCourse';

import { AnswerActionButton } from './AnswerActionButton';
import type { MultipleChoiceQuestionData } from './questionNormalizer';
import { QuestionOptionRow } from './QuestionOptionRow';
import type { NormalizedAnswerInput } from './useQuestionSubmission';
import styles from './MultipleChoiceQuestion.module.css';

interface MultipleChoiceQuestionProps {
  areSolutionsVisible: boolean;
  hideStats?: boolean;
  /** The answer is kept on the device until the server has it: no "Saved" tag. */
  isAnswerKept?: boolean;
  isSectionClosed: boolean;
  isSectionOpen: boolean;
  onCheck(answer: NormalizedAnswerInput): Promise<void>;
  /** Told whether the answer on screen differs from the one stored. */
  onEntryChange?(isUnsaved: boolean): void;
  onSave(answer: NormalizedAnswerInput): Promise<void>;
  pending: boolean;
  question: MultipleChoiceQuestionData;
  /** The request the service worker kept and the server has since refused. */
  refusedSubmissionId?: string;
  role: UserRole;
  showActions?: boolean;
}

export function MultipleChoiceQuestion({
  areSolutionsVisible,
  hideStats = false,
  isAnswerKept = false,
  isSectionClosed,
  isSectionOpen,
  onCheck,
  onEntryChange,
  onSave,
  pending,
  question,
  refusedSubmissionId,
  role,
  showActions = true,
}: MultipleChoiceQuestionProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    question.selectedIndex,
  );
  const [savedIndex, setSavedIndex] = useState<number | undefined>(
    question.selectedIndex,
  );
  const [checkedIndex, setCheckedIndex] = useState<number | undefined>(
    question.attemptsUsed > 0 && question.feedback?.status !== 'saved'
      ? question.selectedIndex
      : undefined,
  );
  const previousQuestionIdRef = useRef(question.id);

  useEffect(() => {
    const isSameQuestion = previousQuestionIdRef.current === question.id;
    previousQuestionIdRef.current = question.id;

    // What the student has changed and not saved stays while they can still change
    // it. An older answer replayed from the queue put itself back on screen, and a
    // Check then graded that and used an attempt.
    if (!isSameQuestion || !isEntryUnsaved || interaction.isAnswerLocked) {
      setSelectedIndex(question.selectedIndex);
    }

    setSavedIndex(question.selectedIndex);
    setCheckedIndex((currentCheckedIndex) => {
      if (question.attemptsUsed > 0 && question.feedback?.status !== 'saved') {
        return question.selectedIndex;
      }

      if (
        isSameQuestion &&
        question.attemptsUsed > 0 &&
        question.feedback?.status === 'saved'
      ) {
        // A save after a check keeps the checked result — but ONLY while the
        // saved answer is still the answer that was checked. Saving a different
        // answer replaces what will be graded at the due date, so the old
        // verdict no longer describes the student's answer and must not keep
        // sitting on the option it was about.
        return question.selectedIndex === currentCheckedIndex
          ? currentCheckedIndex
          : undefined;
      }

      return undefined;
    });
  }, [
    question.attemptsUsed,
    question.feedback?.status,
    question.id,
    question.selectedIndex,
  ]);

  const hasAnswer = selectedIndex !== undefined;
  const hasSelectionChangedFromSaved = hasAnswer && selectedIndex !== savedIndex;
  const hasSavedAnswer = hasAnswer && selectedIndex === savedIndex;
  // A check stores the answer it grades, so Check takes the answer on screen,
  // saved or not, unless it is the one last checked (issue #92).
  const isAnswerAlreadyChecked = hasAnswer && selectedIndex === checkedIndex;
  const isEntryUnsaved = selectedIndex !== savedIndex;

  useEffect(() => {
    onEntryChange?.(isEntryUnsaved);
  }, [isEntryUnsaved, onEntryChange]);

  const earnedPoints =
    question.feedback?.earnedPoints ?? question.scoreEarned;
  // The lock threshold is the feedback's own possible total when present: under
  // hybrid participation weight the achievable max exceeds pointsPossible (award
  // for answering + points for correctness), and a wrong answer earning only the
  // participation award must NOT lock the question as "max earned". Every
  // non-hybrid feedback branch sets possiblePoints === pointsPossible, so this is
  // behavior-preserving elsewhere.
  const maxPointsThreshold =
    question.feedback?.possiblePoints ?? question.pointsPossible;
  const hasEarnedMaxPoints =
    earnedPoints !== undefined &&
    maxPointsThreshold > 0 &&
    earnedPoints >= maxPointsThreshold;
  const interaction = useQuestionInteractionState({
    attemptsAllowed: question.maxAttempts,
    attemptsUsed: question.attemptsUsed,
    areSolutionsVisible,
    hasCheckableAnswer: hasAnswer && !isAnswerAlreadyChecked,
    hasEarnedMaxPoints,
    hasCheckedAtLeastOnce:
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved',
    hasSavedAnswer,
    hasSelectionChangedFromSaved,
    hideStatsAndSolutions: hideStats,
    hintSetting: question.hintSetting,
    isPractice: !showActions || !question.isAssigned,
    isSectionClosed,
    isSectionOpen,
    isSingleAttemptMode: question.isSingleAttemptMode,
    role,
  });

  // Kept by the service worker, this answer was recorded as saved, which took the
  // stored answer's verdict away. The server has now refused it, which changes nothing
  // stored, so the effect above does not run. It stays on screen, as unsaved, while it
  // can still be changed; options that cannot show what is stored.
  useEffect(() => {
    if (!refusedSubmissionId) {
      return;
    }

    if (interaction.isAnswerLocked) {
      setSelectedIndex(question.selectedIndex);
    }

    setSavedIndex(question.selectedIndex);
    setCheckedIndex(
      question.attemptsUsed > 0 && question.feedback?.status !== 'saved'
        ? question.selectedIndex
        : undefined,
    );
  }, [
    interaction.isAnswerLocked,
    question.attemptsUsed,
    question.feedback?.status,
    question.selectedIndex,
    refusedSubmissionId,
  ]);

  const answer: NormalizedAnswerInput = {
    ...(question.currentRandomExactAnswer !== undefined
      ? { currentRandomExactAnswer: question.currentRandomExactAnswer }
      : {}),
    ...(question.currentRandomNumericalAnswer !== undefined
      ? {
          currentRandomNumericalAnswer:
            question.currentRandomNumericalAnswer,
        }
      : {}),
    ...(question.currentRandomNumericalAnswerExtraExponent !== undefined
      ? {
          currentRandomNumericalAnswerExtraExponent:
            question.currentRandomNumericalAnswerExtraExponent,
        }
      : {}),
    kind: 'multipleChoice',
    ...(selectedIndex !== undefined ? { selectedIndex } : {}),
  };

  async function handleSave() {
    if (!hasAnswer) {
      return;
    }

    // Saving always saves — never grades. Single-attempt questions have no Check
    // button, but their Save must NOT reveal correctness or consume the answer:
    // students revise and re-save until the due date (the backend may still score
    // the save, but that is not surfaced until the section closes).
    //
    // onSave rejects when the answer was not stored (the card has told the
    // student). Then nothing below runs: the entry stays unsaved, and Save
    // stays available. It used to be marked "Saved" whatever happened.
    try {
      await onSave(answer);
    } catch {
      return;
    }

    setSavedIndex(selectedIndex);
    // The saved answer is what will be graded from here on, so a verdict from a
    // previously checked DIFFERENT answer no longer applies and its tag must go.
    // Re-saving the same answer changes nothing and keeps its result.
    setCheckedIndex((currentCheckedIndex) =>
      currentCheckedIndex === selectedIndex ? currentCheckedIndex : undefined,
    );
  }

  async function handleCheck() {
    if (!hasAnswer) {
      return;
    }

    // As for Save: a check that was not stored leaves no verdict behind.
    try {
      await onCheck(answer);
    } catch {
      return;
    }

    setSavedIndex(selectedIndex);
    setCheckedIndex(selectedIndex);
  }

  const showCorrectAnswer = interaction.showCorrectAnswer;
  const checkedAnswerState =
    question.feedback?.status === 'correct'
      ? 'correct'
      : question.feedback?.status === 'partial'
        ? 'partial'
        : question.feedback?.status === 'incorrect'
        ? 'incorrect'
        : checkedIndex !== undefined &&
            question.correctAnswerIndex !== undefined
          ? checkedIndex === question.correctAnswerIndex
            ? 'correct'
            : 'incorrect'
          : undefined;
  const showDistributionStats =
    question.isInstructorView &&
    !hideStats &&
    (question.answerIndexStats?.length ?? 0) > 0;
  const showActionRow =
    showActions && (interaction.showSaveButton || interaction.showCheckButton);

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Choose one answer</legend>

      <div className={styles.optionList}>
        {question.options.map((option) => {
          const inputId = `${question.id}-option-${option.index}`;
          const isCorrect = question.correctAnswerIndex === option.index;
          const isSelected = selectedIndex === option.index;
          const isSavedOption =
            role === 'student' &&
            question.isAssigned &&
            !isAnswerKept &&
            savedIndex !== undefined &&
            savedIndex === option.index &&
            savedIndex !== checkedIndex;
          const isSubmittedOption =
            role === 'student' &&
            question.isAssigned &&
            checkedIndex !== undefined &&
            checkedIndex === option.index &&
            checkedAnswerState !== undefined;

          return (
            <QuestionOptionRow
              checkedAnswerState={checkedAnswerState}
              correctIndexForStats={question.correctAnswerIndex}
              disabled={interaction.isAnswerLocked}
              inputId={inputId}
              // Radios share a name so the browser enforces single-select.
              inputName={question.id}
              inputType="radio"
              isCorrect={isCorrect}
              isSaved={isSavedOption}
              isSelected={isSelected}
              isSubmitted={isSubmittedOption}
              key={option.id}
              onToggle={() => setSelectedIndex(option.index)}
              option={option}
              role={role}
              showCorrectAnswer={showCorrectAnswer}
              showDistributionStats={showDistributionStats}
              stats={question.answerIndexStats}
              units={question.units}
            />
          );
        })}
      </div>

      {showActionRow ? (
        <div className={styles.actions}>
          {interaction.showSaveButton ? (
            <AnswerActionButton
              onPress={() => {
                void handleSave();
              }}
              questionId={question.id}
              unavailable={!hasAnswer || interaction.isSaveButtonDisabled || pending}
              variant="secondary"
            >
              Save answer
            </AnswerActionButton>
          ) : null}
          {interaction.showCheckButton ? (
            <AnswerActionButton
              onPress={() => {
                void handleCheck();
              }}
              questionId={question.id}
              unavailable={!hasAnswer || interaction.isCheckButtonDisabled || pending}
              variant="primary"
            >
              Check answer
            </AnswerActionButton>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}
